import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Dumbbell, Shield, GraduationCap, Stethoscope, Scissors, ShoppingBag, ArrowUpRight } from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";

const verticals = [
  {
    id: "gimnasios",
    icon: Dumbbell,
    label: "Gimnasios y Fitness Studios",
    headline: "Cobra tus membresías mensuales sin que nadie se atrase.",
    subtitle: "CrossFit, pilates, yoga y entrenamiento funcional con cobros automáticos.",
    pains: [
      "Miembros que \u201Cse les olvida\u201D pagar y te dejan el mes en el aire",
      "Tarjetas vencidas que descubres cuando ya el cliente se desconectó",
      "Cobros mezclados: efectivo por un lado, transferencias por otro, y nadie concilia",
    ],
    wins: [
      "Cobro mensual automático en la fecha que tú defines",
      "Reintentos automáticos cuando un pago falla (sin que el coach persiga)",
      "Efectivo, transferencia y pagos online en un solo historial, con visibilidad real",
    ],
  },
  {
    id: "instituciones",
    icon: Shield,
    label: "Instituciones deportivas",
    headline: "Mensualidades e inscripciones cobradas sin perseguir a nadie.",
    pains: [
      "Pagos que dependen de \u201Cte transfiero luego\u201D y se vuelven atraso cada mes",
      "Inscripciones/renovaciones por temporada que se cobran tarde o se pierden en el caos",
      "Padres o alumnos que cambian tarjeta y nadie se entera hasta que ya faltaron semanas",
    ],
    wins: [
      "Cobro recurrente automático para mensualidades, grupos y planes por nivel",
      "Reintentos automáticos cuando un pago falla (sin que tu staff haga de cobrador)",
      "Portal para que el cliente/padre actualice su método de pago sin escribirte",
    ],
    microcopy: "En la demo lo aterrizamos a tu deporte y tu modelo de cobro en 15 minutos.",
  },
  {
    id: "escuelas",
    icon: GraduationCap,
    label: "Colegios y academias",
    headline: "Colegiatura y talleres cobrados sin llamadas ni recordatorios.",
    pains: [
      "Padres que pagan tarde y te cambian el flujo del mes completo",
      "Cobrar implica conversaciones incómodas y desgaste administrativo",
      "Cuentas por cobrar que crecen y nadie tiene control claro",
    ],
    wins: [
      "Cobro recurrente en fechas fijas para colegiaturas y talleres",
      "Reglas de suspensión automatizadas sin \u201Cpelea\u201D ni llamadas del director",
      "Portal para que los padres actualicen su método de pago sin pasar por tu equipo",
    ],
  },
  {
    id: "clinicas",
    icon: Stethoscope,
    label: "Clínicas y estética",
    headline: "Paquetes y membresías de tratamientos, en piloto automático.",
    pains: [
      "Paquetes a plazos difíciles de administrar (y fáciles de olvidar)",
      "Cliente que agenda, llega y \u201Cresuelve el pago después\u201D",
      "Combinas consulta única con planes recurrentes y se te enreda la operación",
    ],
    wins: [
      "Paquetes y tratamientos en cuotas con cobro automático (según tus condiciones)",
      "Cobro previo con enlace de pago antes de la cita para reducir cancelaciones",
      "Recurrente para membresías VIP + pago único para consultas, todo en un mismo sistema",
    ],
  },
  {
    id: "barberias",
    icon: Scissors,
    label: "Barberías y salones",
    headline: "Planes recurrentes y caja ordenada, sin perseguir a nadie.",
    pains: [
      "Planes mensuales que dependen de recordatorios y \u201Cte transfiero luego\u201D",
      "Mucho efectivo suelto y cierres de caja que nunca cuadran perfecto",
      "Clientes recurrentes que cancelan/pausan sin avisar y te dejan huecos",
    ],
    wins: [
      "Cobro recurrente para membresías (cortes, uñas, color, estética) sin persecución",
      "Historial unificado para ver quién pagó, quién se atrasó y qué se debe",
      "Portal para que el cliente actualice su método de pago o pause, sin escribirte",
    ],
  },
  {
    id: "servicios",
    icon: ShoppingBag,
    label: "Servicios por suscripción",
    headline: "Convierte pagos repetidos en cobros automáticos (sin caos).",
    pains: [
      "Pagos repetidos que dependen de \u201Cacuerdos\u201D y terminan en atraso",
      "Renovaciones manuales que consumen tiempo y se vuelven error humano",
      "Falta de visibilidad: sabes que vendes, pero no sabes qué se cobra y qué se cae",
    ],
    wins: [
      "Cobros automáticos en la frecuencia que tu negocio necesite",
      "Menos fricción: el cliente paga una vez y luego el sistema se encarga",
      "Control total: ingresos recurrentes más predecibles y operación más ordenada",
    ],
  },
];

export default function Verticals({ onDemoClick }) {
  const [active, setActive] = useState(verticals[0].id);
  const ref = useRef(null);
  const tabsRef = useRef(null);
  const panelContentRef = useRef(null);
  const isMobile = useIsMobile();
  const isBelowLg = useIsMobile(1024);
  const prefersReducedMotion = useReducedMotion();

  const [panelHeight, setPanelHeight] = useState("auto");
  const [scrollState, setScrollState] = useState("start");

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yBg = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [30, -30]);

  const updateScrollState = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    const roundedLeft = Math.round(scrollLeft);
    const threshold = 24;
    if (maxScroll <= 1) {
      setScrollState("none");
    } else if (roundedLeft <= threshold) {
      setScrollState("start");
    } else if (roundedLeft >= maxScroll - threshold) {
      setScrollState("end");
    } else {
      setScrollState("middle");
    }
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = tabsRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  useLayoutEffect(() => {
    if (panelContentRef.current) {
      setPanelHeight(panelContentRef.current.offsetHeight);
    }
  }, [active]);

  const current = verticals.find((v) => v.id === active);
  const Icon = current.icon;

  const tabsMask =
    !isBelowLg || scrollState === "none"
      ? "none"
      : scrollState === "start"
      ? "linear-gradient(to right, black 0, black calc(100% - 24px), transparent 100%)"
      : scrollState === "end"
      ? "linear-gradient(to right, transparent 0, black 24px, black 100%)"
      : "linear-gradient(to right, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)";

  return (
    <motion.section
      id="verticals"
      ref={ref}
      data-testid="verticals-section"
      className="relative py-10 md:py-12 bg-[#F2EDE3] overflow-hidden"
    >
      <motion.div
        style={{ y: prefersReducedMotion ? 0 : yBg }}
        className="absolute -bottom-20 -left-40 w-[500px] h-[500px] rounded-full bg-[#a68a6d]/5 blur-3xl pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative max-w-8xl mx-auto px-4 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-8 md:mb-16"
        >
          <span className="text-sm md:text-base font-bold uppercase tracking-[0.2em] text-[#111111]">
            Hecho para tu vertical
          </span>
          <h2 className="mt-3 md:mt-4 font-['Cabinet_Grotesk'] font-black tracking-tighter leading-[0.95] text-[#075a22] text-3xl sm:text-4xl md:text-6xl">
            Un CRM que <span className="text-[#a68a6d]">entiende</span> tu
            negocio.
          </h2>
          <p className="mt-3 md:mt-5 text-base md:text-xl text-[#111111]/85 max-w-2xl">
            No importa si vendes clases, cortes o consultas. Kubo Payments se
            adapta a cómo cobras hoy y arregla lo que no funciona.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Tabs */}
          <div className="lg:col-span-4 min-w-0">
            <div
              ref={tabsRef}
              className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible no-scrollbar snap-x snap-mandatory pb-2 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0"
              style={{
                maskImage: tabsMask,
                WebkitMaskImage: tabsMask,
              }}
            >
              {verticals.map((v) => {
                const VIcon = v.icon;
                const isActive = active === v.id;
                return (
                  <button
                    key={v.id}
                    onClick={() => setActive(v.id)}
                    data-testid={`vertical-tab-${v.id}`}
                    className={`shrink-0 snap-start flex items-center gap-2.5 md:gap-3 text-left px-4 md:px-6 py-3.5 md:py-5 rounded-xl md:rounded-2xl border transition-all duration-200 ${
                      isActive
                        ? "bg-[#111111] border-[#111111] text-white shadow-[0_8px_24px_-8px_rgba(17,17,17,0.35)]"
                        : "bg-white border-[#111111]/10 text-[#111111] hover:border-[#a68a6d]/40"
                    }`}
                  >
                    <div
                      className={`w-9 h-9 md:w-10 md:h-10 rounded-lg md:rounded-xl flex items-center justify-center shrink-0 transition-colors duration-200 ${
                        isActive ? "bg-[#a68a6d]" : "bg-[#111111]/5"
                      }`}
                    >
                      <VIcon
                        className={`w-4 h-4 md:w-5 md:h-5 ${
                          isActive ? "text-white" : "text-[#111111]"
                        }`}
                        strokeWidth={2}
                      />
                    </div>
                    <span className="font-['Cabinet_Grotesk'] font-bold text-sm md:text-base lg:text-lg leading-tight whitespace-nowrap">
                      {v.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Panel */}
          <div className="lg:col-span-8 relative min-w-0">
            <div className="bg-white border border-[#111111]/10 rounded-2xl md:rounded-3xl shadow-[0_16px_40px_-25px_rgba(17,17,17,0.18)] md:shadow-[0_20px_60px_-30px_rgba(17,17,17,0.2)] overflow-hidden">
              <motion.div
                animate={{ height: isBelowLg ? "auto" : panelHeight }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
                initial={false}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={active}
                    ref={panelContentRef}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: isMobile ? 0.25 : 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="p-5 md:p-8 lg:p-12"
                  >
                    <PanelContent current={current} Icon={Icon} active={active} onDemoClick={onDemoClick} isMobile={isMobile} />
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function PanelContent({ current, Icon, active, onDemoClick, isMobile }) {
  return (
    <>
      <div className="flex items-center gap-2.5 md:gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-[#a68a6d]/10 flex items-center justify-center">
          <Icon className="w-5 h-5 md:w-6 md:h-6 text-[#a68a6d]" strokeWidth={2} />
        </div>
        <span className="text-sm md:text-base font-bold uppercase tracking-[0.18em] text-[#111111]/85">
          {current.label}
        </span>
      </div>

      <h3 className="font-['Cabinet_Grotesk'] font-black tracking-tight text-[#075a22] text-2xl md:text-4xl lg:text-5xl leading-[1.1] md:leading-tight max-w-2xl break-words">
        {current.headline}
      </h3>
      {current.subtitle && (
        <p className="mt-2 md:mt-3 text-base md:text-xl text-[#111111]/75 max-w-2xl break-words">
          {current.subtitle}
        </p>
      )}

      <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-10">
        <div>
          <p className="text-sm md:text-base font-bold uppercase tracking-[0.18em] text-[#111111]/85 mb-3 md:mb-4">
            Lo que te pasa hoy
          </p>
          <ul className="space-y-2.5 md:space-y-3">
            {current.pains.map((p, i) => (
              <motion.li
                key={`${active}-pain-${i}`}
                initial={isMobile ? false : { opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: 0.08 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-start gap-2.5 md:gap-3 text-sm md:text-base text-[#111111]/85 leading-relaxed"
              >
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#111111]/30 shrink-0" />
                {p}
              </motion.li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs md:text-sm font-bold uppercase tracking-[0.18em] text-[#111111] mb-3 md:mb-4">
            Lo que resolvemos
          </p>
          <ul className="space-y-2.5 md:space-y-3">
            {current.wins.map((w, i) => (
              <motion.li
                key={`${active}-win-${i}`}
                initial={isMobile ? false : { opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: 0.1 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-start gap-2.5 md:gap-3 text-sm md:text-base text-[#111111] leading-relaxed font-medium"
              >
                <svg
                  className="mt-0.5 w-4 h-4 text-[#a68a6d] shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {w}
              </motion.li>
            ))}
          </ul>
        </div>
      </div>

      {current.microcopy && (
        <p className="mt-5 md:mt-6 text-sm md:text-base text-[#111111]/70 italic">
          {current.microcopy}
        </p>
      )}

      <button
        onClick={() => onDemoClick(current.label)}
        data-testid={`vertical-cta-${active}`}
        className="mt-7 md:mt-10 inline-flex items-center gap-2 bg-[#a68a6d] hover:bg-[#8f7659] text-white text-sm md:text-base font-bold px-5 md:px-7 py-3 md:py-4 rounded-full transition-all hover:scale-105 shadow-[0_10px_30px_rgba(166,138,109,0.25)] break-words text-left max-w-full"
      >
        Agendar demo para {current.label.toLowerCase()}
        <ArrowUpRight className="w-4 h-4 shrink-0" strokeWidth={2.5} />
      </button>
    </>
  );
}
